import React, {
  useState,
  useCallback,
  useMemo,
  memo,
  useEffect,
  useRef,
  Suspense,
  lazy,
} from 'react'
import { withRouter, RouteComponentProps, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import Icon from 'components/Shared/Icon'
import Button from 'components/Shared/Button'
import { GroupedAccordion } from 'components/Shared/Accordion'

import {
  setAuditData,
  getAuditData,
  AuditData,
  clearAuditData,
} from 'ducks/x-ray'
import { selectObjects, getMap, State } from 'ducks/editor/objects'
import getObject from 'ducks/editor/objects/helpers/getObject'
import centerScreenOnCanvas from 'utils/objects/centerScreenOnCanvas'
import { requestAudit } from 'utils/io/x-ray'

// Import components and utilities
import LoadingMessage from './LoadingMessage'
import ErrorContent from './ErrorContent'

import './x-ray.scss'

// Lazy load accordion sections for better bundle splitting
const NestedListsSection = lazy(() => import('./components/NestedListsSection'))
const ComplexitySection = lazy(() => import('./components/ComplexitySection'))
const CacheHitRatiosSection = lazy(
  () => import('./components/CacheHitRatiosSection')
)
const OrphanedScreensSection = lazy(
  () => import('./components/OrphanedScreensSection')
)
const LiveRowCountsSection = lazy(
  () => import('./components/LiveRowCountsSection')
)

// Preload lazy components to avoid loading states after audit completes
const preloadLazyComponents = async () => {
  try {
    await Promise.all([
      import('./components/NestedListsSection'),
      import('./components/ComplexitySection'),
      import('./components/CacheHitRatiosSection'),
      import('./components/OrphanedScreensSection'),
      import('./components/LiveRowCountsSection'),
    ])
  } catch (error) {
    console.warn('Failed to preload some components:', error)
  }
}

// Lightweight loading component for accordion sections
const AccordionLoadingFallback: React.FC = () => (
  <div
    className="accordion-loading"
    style={{ padding: '20px', textAlign: 'center' }}
  >
    <div>Loading section...</div>
  </div>
)

interface AuditingPanelProps extends RouteComponentProps {
  appId: string
}

const AuditingPanel: React.FC<AuditingPanelProps> = ({ appId }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const objectList = useSelector(selectObjects)
  const pathMap = useSelector(getMap)
  const auditData = useSelector((state: State) => getAuditData(state, appId))

  const [isAuditing, setIsAuditing] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<number>(0.01)

  // Ref to track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true)

  // Ref to store abort controller for cleanup
  const abortControllerRef = useRef<AbortController | null>(null)

  // Effect to check for existing data and auto-start auditing
  useEffect(() => {
    if (auditData) {
      setIsAuditing(true)
      setIsRefreshing(false)
    }
  }, [auditData])

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchAuditData = useCallback(
    async (isRefresh = false) => {
      // Abort any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()

      // Set loading state immediately
      if (isRefresh) {
        setIsRefreshing(true)
      } else {
        setIsAuditing(true)
        setIsLoading(true)
      }

      // Don't fetch if we already have data and it's not a refresh
      if (auditData && !isRefresh) {
        setIsAuditing(true)
        setIsLoading(false)

        return
      }

      if (isRefresh) {
        dispatch(clearAuditData(appId))
      }

      setError(null)

      try {
        // Start preloading components while audit is running
        const preloadPromise = preloadLazyComponents()

        // Request the audit
        const response = await requestAudit({
          appId,
          onProgress: (chunkProgress: number) => {
            setProgress(chunkProgress)
          },
        })
        // Wait for both audit and component preloading to complete
        await preloadPromise

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          // Set the audit data
          dispatch(setAuditData(appId, response as AuditData))
        }
      } catch (err) {
        // Only handle error if component is still mounted
        if (isMountedRef.current) {
          console.error('Error performing audit:', err)
          setError('Failed to perform audit. Please try again.')
        }
      } finally {
        // Only update loading state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false)
          setIsRefreshing(false)
          setProgress(0.01)
        }
      }
    },
    [appId, auditData, dispatch]
  )

  const handleScreenClick = useCallback(
    (screenId: string, componentId?: string) => {
      try {
        const screenObject = getObject(objectList, pathMap, screenId)
        history.push(`/apps/${appId}/screens`)
        dispatch(centerScreenOnCanvas(screenObject, componentId))
      } catch (err) {
        console.error('Error centering screen:', err)
      }
    },
    [dispatch, objectList, pathMap]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, screenId: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleScreenClick(screenId)
      }
    },
    [handleScreenClick]
  )

  const introSection = useMemo(
    () => (
      <div className="x-ray-intro">
        <h3>Welcome to Adalo X-Ray</h3>
        <p>
          Adalo X-Ray is an experimental tool intended to help Makers build more
          performant apps. We welcome your feedback! Email us at{' '}
          <a href="mailto:support@adalo.com">support@adalo.com</a> with comments
          and questions.
        </p>
        <Button
          onClick={() => fetchAuditData(false)}
          teal
          icon="search"
          medium
          disabled={isLoading}
        >
          {isLoading ? 'Starting Audit...' : 'Start Audit'}
        </Button>
      </div>
    ),
    [fetchAuditData, isLoading]
  )

  const auditContent = useMemo(() => {
    if (error) {
      return (
        <div className="x-ray-content">
          <ErrorContent
            error={error}
            isRefreshing={isRefreshing}
            onRetry={() => {
              fetchAuditData(true).catch(err => {
                console.error('Error fetching audit data:', err)
              })
            }}
          />
        </div>
      )
    }

    if (isLoading || isRefreshing) {
      return (
        <div className="x-ray-content">
          <LoadingMessage progress={progress} />
        </div>
      )
    }

    return (
      <div className="x-ray-content">
        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Nested Lists</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <NestedListsSection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Screen Complexity</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <ComplexitySection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Orphaned Screens</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <OrphanedScreensSection
                auditData={auditData}
                handleScreenClick={handleScreenClick}
                handleKeyDown={handleKeyDown}
              />
            </Suspense>
          )}
        />
        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Database Tables</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <LiveRowCountsSection auditData={auditData} appId={appId} />
            </Suspense>
          )}
        />

        <GroupedAccordion
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          defaultExpanded={false}
          title={<div className="x-ray-title-inner">Cache Hit Ratios</div>}
          className="x-ray"
          renderChildren={() => (
            <Suspense fallback={<AccordionLoadingFallback />}>
              <CacheHitRatiosSection auditData={auditData} />
            </Suspense>
          )}
        />

        <div className="x-ray-intro x-ray-button-container">
          <Button
            onClick={() => fetchAuditData(true)}
            teal
            icon="refresh"
            medium
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing Audit...' : 'Audit Again'}
          </Button>
        </div>

        <div className="x-ray-feedback">
          <Icon type="contact-email" />
          Have a feedback? Email us at
          <a href="mailto:xray@adalo.com">xray@adalo.com</a>
        </div>
      </div>
    )
  }, [
    auditData,
    handleScreenClick,
    handleKeyDown,
    fetchAuditData,
    isLoading,
    isRefreshing,
    error,
    progress,
  ])

  return (
    <div className="x-ray-audit-wrapper">
      <div className="left-panel-header">
        <h2 className="left-panel-title">
          <Icon type="link" />
          Audit Your App
        </h2>
      </div>

      {!isAuditing ? introSection : auditContent}
    </div>
  )
}

export default memo(withRouter(AuditingPanel))
